import {inputOwnsWhiteboard} from "../TaskAssessmentPanel";
﻿// src/components/QuizRunner/UniversalQuizRunner.js
import React, { useState, useContext, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { BlockMath, InlineMath } from "react-katex";
import "katex/dist/katex.min.css";

import { AuthContext } from "../AuthContext";
import { saveLessonResult } from "../../utils/saveProgress";
import { quizTypeRegistry } from "./quizTypeRegistry";
import { formatLatexPreview } from "../../components/inputs/FractionInput";

import EdgeDrawer from "../ui/EdgeDrawer";
import ExamCalculator from "../ExamCalculator";
import FormulaSheet from "../FormulaSheet";
import HorizontalScroll from "../ui/HorizontalScroll";
import useFabTop from "../ui/useFabTop";
import TaskInlineWhiteboard from "../TaskRunner/TaskInlineWhiteboard";
import { useLocation, useNavigate } from "react-router-dom";
import { isDemoPath } from "../../demo/demoAccess";
import MatematronModal from "../ui/MatematronModal";
import { parseIntervalInput } from "../inputs/IntervalInput";
import { detectHasAnswer } from "../MaturaExamRunner/MaturaExamSnapshotUtils";
import useUnsavedQuizAttemptGuard from "../../hooks/useUnsavedQuizAttemptGuard";
import MaturaOriginalSourceLinks from "../MaturaOriginalSourceLinks";
import { UNSAVED_QUIZ_MESSAGE } from "../../utils/unsavedQuizGuard";
import { generateDifferentTaskSet } from "./quizTaskRegeneration";
import "./UniversalQuizRunner.css";
import { hasIncompleteQuizAnswers } from "./incompleteQuizAnswers";
import {
  buildLearningDraftKey,
  readLearningDraft,
  removeLearningDraft,
  writeLearningDraft,
} from "../../utils/localLearningDrafts";


// Ścieżka do PDF z kartą wzorów (wrzuć plik do public/assets/)
const WZORY_URL =
  process.env.PUBLIC_URL + "/assets/wybrane_wzory_matematyczne_EM2023.pdf";

const QUIZ_SAVE_QUEUE_KEY = "quiz_progress_queue_v1";
const QUIZ_SAVE_MAX_QUEUE_ITEMS = 30;
const QUIZ_SAVE_MAX_ATTEMPTS = 8;
const QUIZ_SAVE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const QUIZ_SAVE_RETRY_DELAYS_MS = [0, 5000, 15000, 60000, 180000, 600000, 1800000, 3600000];
const LIVE_SAVE_RETRY_DELAYS_MS = [0, 1200, 3500];
const LEARNING_DRAFT_COMPONENTS = { BlockMath, InlineMath };

function readQuizSaveQueue() {
  try {
    const raw = localStorage.getItem(QUIZ_SAVE_QUEUE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeQuizSaveQueue(queue) {
  try {
    localStorage.setItem(QUIZ_SAVE_QUEUE_KEY, JSON.stringify(queue));
    return true;
  } catch {
    return false;
  }
}

function enqueueQuizSave(payload) {
  const now = Date.now();
  const queue = readQuizSaveQueue();

  const pruned = queue.filter((item) => {
    const createdAtTs = item?.createdAtTs || Date.parse(item?.createdAt || "");
    if (!Number.isFinite(createdAtTs)) return false;
    return now - createdAtTs <= QUIZ_SAVE_MAX_AGE_MS;
  });

  const item = {
    id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    createdAt: new Date(now).toISOString(),
    createdAtTs: now,
    attempts: 0,
    nextRetryAtTs: now,
    lastError: null,
    payload,
  };

  pruned.push(item);

  const bounded =
    pruned.length > QUIZ_SAVE_MAX_QUEUE_ITEMS
      ? pruned.slice(pruned.length - QUIZ_SAVE_MAX_QUEUE_ITEMS)
      : pruned;

  const ok = writeQuizSaveQueue(bounded);
  return { ok, id: item.id };
}

function estimateJsonSize(value) {
  try {
    return new Blob([JSON.stringify(value)]).size;
  } catch {
    return null;
  }
}

function getRetryDelayMs(attemptNumber) {
  const idx = Math.max(0, Math.min(attemptNumber, QUIZ_SAVE_RETRY_DELAYS_MS.length - 1));
  return QUIZ_SAVE_RETRY_DELAYS_MS[idx];
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Pływający przycisk renderowany do body (jak w TaskRunner) */
function Fab({ side = "left", top = 96, title, ariaLabel, icon, onClick }) {
  const [node, setNode] = useState(null);
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);

  useEffect(() => {
    const el = document.createElement("div");
    document.body.appendChild(el);
    setNode(el);
    return () => document.body.removeChild(el);
  }, []);

  if (!node) return null;

  const base = {
    position: "fixed",
    [side]: 12,
    top,
    zIndex: 998,
    width: 56,
    height: 56,
    display: "grid",
    placeItems: "center",
    cursor: "pointer",
    borderRadius: "9999px",
    background: "#e58b00",
    color: "#171717",
    border: "1px solid rgba(23,23,23,.82)",
    boxShadow: "0 4px 12px rgba(0,0,0,.30)",
    transition: "top .12s ease-out, transform .12s ease, box-shadow .18s ease",
  };
  const hover = hovered
    ? {
        background: "#f5a31a",
        transform: "translateY(-1px) scale(1.03)",
        boxShadow: "0 6px 16px rgba(0,0,0,.34)",
      }
    : {};
  const active = pressed ? { transform: "translateY(0) scale(0.97)" } : {};

  return createPortal(
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => {
        setHovered(false);
        setPressed(false);
      }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      aria-label={ariaLabel}
      title={title}
      style={{ ...base, ...hover, ...active }}
    >
      {icon}
    </button>,
    node
  );
}

export default function UniversalQuizRunner({
  sectionTitle,
  snapshotTitle,
  tasks: generatedTasks,
  registry,
  forceLocalMode = false,
  onLocalSnapshot,
  hideUtilityFabs = false,
  onSavedResultDismissed,
  draftScope = "",
  regenerateTasks,
}) {
  const { user, authorizedFetch } = useContext(AuthContext);

  const location = useLocation();
  const navigate = useNavigate();
  const isDemo = isDemoPath(location.pathname);
  const isMaturaCourseQuiz =
    location.pathname.startsWith("/matura/sciezka") ||
    location.pathname.startsWith("/demo/matura/sciezka");
  const isE8CourseQuiz = location.pathname.includes("/egzamin-osmoklasisty/sciezka");
  const savedResultDestination = isE8CourseQuiz ? "/egzamin-osmoklasisty/sciezka" : (isMaturaCourseQuiz || isE8CourseQuiz)
    ? isDemo
      ? "/demo/matura/sciezka"
      : "/matura/sciezka"
    : "/moj-postep";
  const canSave = !!user?.email && !isDemo && !forceLocalMode;
  const draftUserId = isDemo || forceLocalMode ? "guest" : user?.email || "guest";
  const draftKey = useMemo(
    () =>
      buildLearningDraftKey({
        kind: "quiz",
        userId: draftUserId,
        pathname: location.pathname,
        scope: [sectionTitle, draftScope].filter(Boolean).join(":"),
      }),
    [draftScope, draftUserId, location.pathname, sectionTitle]
  );
  const initialDraft = useMemo(
    () => readLearningDraft(draftKey, LEARNING_DRAFT_COMPONENTS),
    [draftKey]
  );
  const generatedTasksRef = useRef(generatedTasks);
  generatedTasksRef.current = generatedTasks;
  const initialTasks =
    Array.isArray(initialDraft?.tasks) && initialDraft.tasks.length
      ? initialDraft.tasks
      : generatedTasks;

  const registryToUse = registry || quizTypeRegistry;

  const [tasks, setTasks] = useState(initialTasks);
  const [userAnswers, setUserAnswers] = useState(() =>
    Array.isArray(initialDraft?.userAnswers) && initialDraft.userAnswers.length === initialTasks.length
      ? initialDraft.userAnswers
      : initialTasks.map(() => "")
  );
  const [draftSavedLocally, setDraftSavedLocally] = useState(Boolean(initialDraft));
  const [showResults, setShowResults] = useState(false);
  const [confirmIncomplete, setConfirmIncomplete] = useState(false);
  const answerContainers = useRef([]);
  const hasUncheckedAnswers =
    !showResults && userAnswers.some((answer) => detectHasAnswer(answer));
  const unsavedAttemptGuard =
    useUnsavedQuizAttemptGuard(hasUncheckedAnswers && !draftSavedLocally);
  const [showExplanation, setShowExplanation] = useState(
    tasks.map(() => false)
  );
  const [score, setScore] = useState(null);
  const [maxScore, setMaxScore] = useState(null); // suma możliwych punktów (obsługa partial)
  const [feedback, setFeedback] = useState([]);
  const [taskWhiteboards, setTaskWhiteboards] = useState(() => (
    Array.isArray(initialDraft?.taskWhiteboards) && initialDraft.taskWhiteboards.length === initialTasks.length
      ? initialDraft.taskWhiteboards
      : initialTasks.map(() => null)
  ));
  const [whiteboardResetToken, setWhiteboardResetToken] = useState(0);
  const [localSaveMessage, setLocalSaveMessage] = useState("");
  const [localSnapshotId, setLocalSnapshotId] = useState("");
  const [rerolling, setRerolling] = useState(false);

  // auto-save po kliknięciu "Sprawdź i zapisz"
  const [pendingAutoSave, setPendingAutoSave] = useState(false);

  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveModalText, setSaveModalText] = useState("");
  const [saveOk, setSaveOk] = useState(true);
  const [saveInProgress, setSaveInProgress] = useState(false);
  const queueFlushRef = useRef(false);

  // Panele: kalkulator (lewa) i karta wzorów (prawa)
  const [calcOpen, setCalcOpen] = useState(false);
  const fabTop = useFabTop();

  const [sheetOpen, setSheetOpen] = useState(false);

  // Skróty: Ctrl+Alt+K (kalkulator), Ctrl+Alt+W (wzory), Esc (zamyka oba)
  useEffect(() => {
    const onKey = (e) => {
      const k = e.key?.toLowerCase();
      if (e.ctrlKey && e.altKey && k === "k") {
        e.preventDefault();
        setCalcOpen((v) => !v);
      }
      if (e.ctrlKey && e.altKey && k === "w") {
        e.preventDefault();
        setSheetOpen((v) => !v);
      }
      if (e.key === "Escape") {
        setCalcOpen(false);
        setSheetOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const activeDraftKeyRef = useRef(draftKey);
  const rerollToken = new URLSearchParams(location.search).get("r") || "";
  const rerollTokenRef = useRef(rerollToken);

  useEffect(() => {
    const draftKeyChanged = activeDraftKeyRef.current !== draftKey;
    const rerollRequested = rerollTokenRef.current !== rerollToken;
    if (!draftKeyChanged && !rerollRequested) return;

    activeDraftKeyRef.current = draftKey;
    rerollTokenRef.current = rerollToken;

    if (rerollRequested) removeLearningDraft(draftKey);
    const restored = rerollRequested
      ? null
      : readLearningDraft(draftKey, LEARNING_DRAFT_COMPONENTS);
    const nextTasks =
      Array.isArray(restored?.tasks) && restored.tasks.length
        ? restored.tasks
        : generatedTasksRef.current;
    const nextAnswers =
      Array.isArray(restored?.userAnswers) && restored.userAnswers.length === nextTasks.length
      ? restored.userAnswers
      : nextTasks.map(() => "");
    const nextWhiteboards =
      Array.isArray(restored?.taskWhiteboards) && restored.taskWhiteboards.length === nextTasks.length
        ? restored.taskWhiteboards
        : nextTasks.map(() => null);

    setTasks(nextTasks);
    setUserAnswers(nextAnswers);
    setShowResults(false);
    setShowExplanation(nextTasks.map(() => false));
    setScore(null);
    setMaxScore(null);
    setFeedback([]);
    setTaskWhiteboards(nextWhiteboards);
    setWhiteboardResetToken((prev) => prev + 1);
    setLocalSaveMessage("");

    if (rerollRequested) {
      const timer = window.setTimeout(() => setRerolling(false), 700);
      return () => window.clearTimeout(timer);
    }
  }, [draftKey, rerollToken]);

  useEffect(() => {
    if (!Array.isArray(tasks) || !tasks.length) return;
    setDraftSavedLocally(writeLearningDraft(draftKey, { tasks, userAnswers, taskWhiteboards }));
  }, [draftKey, taskWhiteboards, tasks, userAnswers]);

  const handleChange = (index, value) => {
    const updated = [...userAnswers];
    updated[index] = value;
    setUserAnswers(updated);
    setDraftSavedLocally(writeLearningDraft(draftKey, {
      tasks,
      userAnswers: updated,
      taskWhiteboards,
    }));
  };

  const toggleExplanation = (index) => {
    const updated = [...showExplanation];
    updated[index] = !updated[index];
    setShowExplanation(updated);
  };

  const handleTaskWhiteboardChange = useCallback((index, boardState) => {
    setTaskWhiteboards((prev) => {
      const next = Array.isArray(prev) ? [...prev] : tasks.map(() => null);
      if (next[index] === (boardState || null)) {
        return prev;
      }
      next[index] = boardState || null;
      return next;
    });
  }, [tasks]);

// helper: ujednolica odpowiedź na potrzeby checkAnswer
// UWAGA: nie spłaszczamy obiektów "strukturalnych" (np. {d1:..., d2:...} albo {d1:...})
const coerceAnswerForCheck = (ua) => {
  if (ua == null) return ua;

  // event z inputa
  if (typeof ua === "object" && ua.target && "value" in ua.target) {
    return ua.target.value ?? "";
  }

  if (typeof ua === "object") {
    if ("parsed" in ua) return ua;

        // ABCD / single-choice: NIE spłaszczaj do latexu, bo checkAnswer potrzebuje choiceIndex
    if ("choiceIndex" in ua && typeof ua.choiceIndex === "number") {
      return ua;
    }

    if ("raw" in ua) return String(ua.raw ?? "");
    if ("latex" in ua) return String(ua.latex ?? "");
    if ("text" in ua) return String(ua.text ?? "");
    if ("expr" in ua) return String(ua.expr ?? "");

    // { value: "..." }
    if ("value" in ua && typeof ua.value !== "object") {
      return String(ua.value ?? "");
    }

    // ⚠️ NIE: nie spłaszczamy już "jednokluczowych" obiektów typu {d1:{raw:"2"}}
    // bo to psuje zadania wielopolowe (d1/d2/d3 itd.)
    const keys = Object.keys(ua);
    if (keys.length === 1) {
      const k = keys[0];
      const v = ua[k];

      // spłaszczaj tylko wtedy, gdy klucz wygląda jak "wrapper", a nie pole formularza
      const wrapperKeys = new Set(["raw", "latex", "text", "expr", "value", "answer"]);
      if (wrapperKeys.has(k)) {
        if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
          return String(v);
        }
        if (v && typeof v === "object" && "raw" in v) {
          return String(v.raw ?? "");
        }
      }
    }
  }

  // zostawiamy tablice i obiekty "strukturalne" dla adapterów, które ich potrzebują
  return ua;
};
  // max punkty dla zadania (także gdy user nic nie wpisał)
  const getTaskMaxPoints = (task, adapter) => {
    // 1) preferuj jawne maxPoints z taska
    const mp = task?.data?.maxPoints ?? task?.maxPoints ?? task?.meta?.maxPoints;
    if (typeof mp === 'number' && mp > 0) return mp;

    // 2) spróbuj dopytać adapter (często zwraca {maxScore})
    if (adapter?.checkAnswer) {
      try {
        const probe = adapter.checkAnswer(task, '');
        if (
          probe &&
          typeof probe === 'object' &&
          typeof probe.maxScore === 'number' &&
          probe.maxScore > 0
        ) {
          return probe.maxScore;
        }
      } catch (e) {
        // ignore
      }
    }

    // 3) fallback
    return 1;
  };


  const checkAnswers = () => {
    let points = 0;
    let possible = 0;
    const feedbackResults = [];

    const hasAnswer = (ua) => {
      if (ua == null) return false;
      if (typeof ua === "string") return ua.trim() !== "";
      if (Array.isArray(ua))
        return ua.some((x) => x != null && String(x).trim() !== "");
      if (typeof ua === "object") {
        // ABCD / choice
        if ("choiceIndex" in ua && typeof ua.choiceIndex === "number") return true;

        if ("raw" in ua) return String(ua.raw || "").trim() !== "";
        if ("value" in ua && typeof ua.value !== "object")
          return String(ua.value ?? "").trim() !== "";

        return Object.values(ua).some((v) => v != null && String(v).trim() !== "");
      }
      return true;
    };


    tasks.forEach((task, index) => {
      const adapter = registryToUse[task.type];
      if (!adapter || !adapter.checkAnswer) {
        feedbackResults.push({ isCorrect: false, score: 0, maxScore: 1 });
        possible += 1;
        return;
      }

      const ua = userAnswers[index];
      const uaForCheck = coerceAnswerForCheck(ua);

      if (!hasAnswer(ua)) {
        const taskMax = getTaskMaxPoints(task, adapter);
        feedbackResults.push({
          isCorrect: false,
          score: 0,
          maxScore: taskMax,
          message: 'Brak odpowiedzi.',
          messageType: 'empty',
        });
        possible += taskMax;
        return;
      }

      let result = false;
        try {
          result = adapter.checkAnswer(task, uaForCheck);
        } catch (e) {
          console.error("[UniversalQuizRunner] checkAnswer crashed:", task?.type, e, {
            userAnswer: uaForCheck,
            rawUserAnswer: userAnswers[index],
          });
          result = false;
        }

      let taskScore = 0;
      let taskMax = 1;

      let isCorrect =
        result === true ||
        (typeof result === "object" && (result.isCorrect || result.correct));

      const needsSimplification =
        typeof result === "object" && result.needsSimplification;

      const expectedLatex =
        typeof result === "object" ? result.expected : null;

      // NOWE: dowolny komunikat z adaptera
      const message =
        typeof result === "object" ? result.message || result.feedbackMessage || result.feedback?.message : null;

      const messageType =
        typeof result === "object" ? result.messageType || result.feedbackType || result.feedback?.type : null;

      if (typeof result === "object" && typeof result.score === "number") {
        taskScore = result.score;
        taskMax = typeof result.maxScore === "number" ? result.maxScore : 1;
        isCorrect = taskScore === taskMax;

        feedbackResults.push({
          isCorrect,
          needsSimplification,
          expectedLatex,
          parts: result.parts,
          score: taskScore,
          maxScore: taskMax,
          message,
          messageType,
        });
      } else {
        taskScore = isCorrect ? 1 : 0;
        taskMax = 1;

        feedbackResults.push({
          isCorrect,
          needsSimplification,
          expectedLatex,
          score: taskScore,
          maxScore: taskMax,
          message,
          messageType,
        });
      }

      points += taskScore;
      possible += taskMax;
    });

    setScore(points);
    setMaxScore(possible);
    setFeedback(feedbackResults);
    setShowResults(true);
    if (forceLocalMode && typeof onLocalSnapshot === "function") {
      const progressPayload = buildProgressPayload({
        user: { email: "guest-session@local.invalid" },
        showResults: true,
        score: points,
        maxScore: possible,
        tasks,
        feedback: feedbackResults,
        userAnswers,
        taskWhiteboards,
        sectionTitle,
      });
      const localSnapshot = onLocalSnapshot({
        sectionTitle,
        snapshotTitle,
        score: points,
        maxScore: possible,
        percent: possible > 0 ? Math.round((points / possible) * 100) : 0,
        feedback: feedbackResults,
        answers: userAnswers,
        tasks,
        taskWhiteboards,
        checkedAt: new Date().toISOString(),
        progressPayload,
      });
      const savedLocally = Boolean(localSnapshot?.id);
      setLocalSnapshotId(localSnapshot?.id || "");
      setSaveOk(savedLocally);
      setSaveModalText(savedLocally
        ? "Podejście zapisaliśmy w tej przeglądarce. Jego podgląd jest dostępny na ścieżce. Kolejne podejście zastąpi ten lokalny zapis."
        : "Wynik został sprawdzony, ale przeglądarka nie pozwoliła zapisać podejścia lokalnie.");
      setSaveModalOpen(true);
      setLocalSaveMessage(savedLocally
        ? "Podejście zapisano lokalnie. Kolejne zastąpi ten zapis."
        : "Nie udało się zapisać lokalnego podejścia.");
    }
  };

    const checkAndSave = (confirmed = false) => {
      if (confirmed !== true && hasIncompleteQuizAnswers(tasks, userAnswers, answerContainers.current)) { setConfirmIncomplete(true); return; }
      setConfirmIncomplete(false);
      // zawsze sprawdzamy
      checkAnswers();

      // jeśli wolno zapisywać (zalogowany, nie demo) — odpal auto-zapis po tym jak UI przejdzie w showResults
      if (canSave) {
        setPendingAutoSave(true);
      } else {
        removeLearningDraft(draftKey);
      }
    };

  // --- helper: zakoduj ±Infinity przed JSON.stringify ---
function encodeInf(val) {
  if (val === Infinity) return "__+inf__";
  if (val === -Infinity) return "__-inf__";
  if (val instanceof Map) {
    return Object.fromEntries(
      [...val.entries()].map(([key, value]) => [String(key), encodeInf(value)])
    );
  }
  if (Array.isArray(val)) return val.map(encodeInf);
  if (val && typeof val === "object") {
    const out = Array.isArray(val) ? [] : {};
    for (const k of Object.keys(val)) out[k] = encodeInf(val[k]);
    return out;
  }
  return val;
}

function toLiteTaskSnapshot(task, statement, correctAnswer) {
  if (!task || typeof task !== "object") return null;
  // Snapshot view still relies on full task payload for many task adapters
  // (prompt rendering, answer rendering, custom structures). Keeping full task
  // avoids regressions like missing statement / [object Object].
  return task;
}

function toLiteWhiteboardSnapshot(source) {
  if (!source || typeof source !== "object") return null;
  const elements = Array.isArray(source.elements) ? source.elements : [];
  if (!elements.length) return null;
  return {
    version: Number(source.version || 1),
    elements,
    pageCount: Math.max(1, Math.min(100, Number(source.pageCount || 1))),
    updatedAt: Number(source.updatedAt || Date.now()),
    updatedBy: source.updatedBy || "snapshot",
  };
}

const buildProgressPayload = ({
  user,
  showResults,
  score,
  maxScore,
  tasks,
  feedback,
  userAnswers,
  taskWhiteboards,
  sectionTitle,
}) => {
  if (!user?.email) return null;
  if (!showResults || score == null) return null;

  const possible = Number.isFinite(maxScore) ? maxScore : tasks.length;
  const normalized = possible > 0 ? score / possible : 0;

  const fmtAnswer = (val) => {
    if (val === null || typeof val === "undefined" || val === "") return "";
    if (typeof val === "string" || typeof val === "number" || typeof val === "boolean") {
      return String(val);
    }
    if (Array.isArray(val)) {
      return val.map((v) => fmtAnswer(v)).filter(Boolean).join(", ");
    }
    if (val && typeof val === "object") {
      for (const key of ["raw", "latex", "text", "expr"]) {
        if (key in val && val[key] != null && typeof val[key] !== "object") {
          return String(val[key]);
        }
      }
      if ("value" in val && val.value != null && typeof val.value !== "object") {
        return String(val.value);
      }
      const parts = Object.entries(val)
        .map(([key, value]) => {
          const text = fmtAnswer(value);
          return text ? `${key}: ${text}` : "";
        })
        .filter(Boolean);
      return parts.join(", ");
    }
    return String(val);
  };

  const questions = tasks.map((task, i) => {
    const fb = feedback[i] || {};
    const ua = userAnswers[i];
    const correct =
      (task?.answer && typeof task.answer === "object" && "answer" in task.answer
        ? task.answer.answer
        : task?.answer) ?? null;
    const statement =
      task?.prompt ??
      task?.title ??
      task?.statement ??
      task?.text ??
      null;

    const wb = taskWhiteboards[i] || ua?.whiteboard;
    const whiteboardToSave = toLiteWhiteboardSnapshot(wb);
    const taskToSave = toLiteTaskSnapshot(task, statement, correct);

    return {
      id: task.id || `q${i + 1}`,
      type: task?.type || task?.meta?.type || task?.questionType || null,
      task: encodeInf(taskToSave),
      userAnswer: encodeInf(ua),
      correctAnswer: encodeInf(correct),
      isCorrect: !!fb.isCorrect,
      score: typeof fb.score === "number" ? fb.score : null,
      maxScore: typeof fb.maxScore === "number" ? fb.maxScore : null,
      parts: fb.parts || null,
      whiteboard: whiteboardToSave,
      display: {
        statement,
        user: fmtAnswer(ua),
        correct: fmtAnswer(correct),
        hasAnswer: detectHasAnswer(ua),
      },
    };
  });

  return {
    taskId: sectionTitle,
    topicId: sectionTitle.split("_").slice(0, 2).join("_"),
    score: normalized,
    maxPoints: possible,
    attemptsInc: 1,
    snapshot: {
      title: snapshotTitle || sectionTitle,
      maxPoints: possible,
      version: "uqr:v1",
      questions,
    },
  };
};


const postProgressPayload = async (payload, debugMeta = {}) => {
  console.log("[quiz-save] PATCH /api/progress:start", {
    saveId: debugMeta.saveId || null,
    taskId: payload?.taskId || null,
    questionCount: Array.isArray(payload?.snapshot?.questions)
      ? payload.snapshot.questions.length
      : 0,
    whiteboardCount: Array.isArray(payload?.snapshot?.questions)
      ? payload.snapshot.questions.filter((q) => q?.whiteboard).length
      : 0,
    payloadBytes: estimateJsonSize(payload),
  });

  const r = await authorizedFetch("/api/progress", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  console.log("[quiz-save] PATCH /api/progress:done", {
    saveId: debugMeta.saveId || null,
    taskId: payload?.taskId || null,
    status: r.status,
    ok: r.ok,
  });

  if (!r.ok) throw new Error(`HTTP ${r.status}`);
};

const flushQueuedQuizSaves = useCallback(async () => {
  if (!authorizedFetch || queueFlushRef.current) return;

  const queue = readQuizSaveQueue();
  if (!queue.length) return;

  console.log("[quiz-save] queue:flush:start", {
    queuedItems: queue.length,
  });

  queueFlushRef.current = true;
  try {
    const now = Date.now();
    const remaining = [];
    for (let i = 0; i < queue.length; i += 1) {
      const item = queue[i] || {};
      const attempts = Number.isFinite(item.attempts) ? item.attempts : 0;
      const createdAtTs = Number.isFinite(item.createdAtTs)
        ? item.createdAtTs
        : Date.parse(item.createdAt || "");
      const nextRetryAtTs = Number.isFinite(item.nextRetryAtTs)
        ? item.nextRetryAtTs
        : 0;

      if (!Number.isFinite(createdAtTs)) {
        // malformed item -> pomijamy
        continue;
      }
      if (now - createdAtTs > QUIZ_SAVE_MAX_AGE_MS) {
        // przeterminowane -> usuwamy z kolejki
        continue;
      }
      if (attempts >= QUIZ_SAVE_MAX_ATTEMPTS) {
        // wyczerpany limit prób -> usuwamy z kolejki
        continue;
      }
      if (nextRetryAtTs > now) {
        remaining.push(item);
        continue;
      }

      try {
        await postProgressPayload(item.payload, { saveId: item.id, source: "queue" });
      } catch (_e) {
        const nextAttempts = attempts + 1;
        const backoffMs = getRetryDelayMs(nextAttempts);
        const updated = {
          ...item,
          attempts: nextAttempts,
          nextRetryAtTs: Date.now() + backoffMs,
          lastError: _e?.message || String(_e),
        };
        console.warn("[quiz-save] queue:flush:stopped", {
          failedSaveId: item.id,
          remainingItems: queue.length - i,
          attempts: nextAttempts,
          nextRetryInMs: backoffMs,
        });
        remaining.push(updated, ...queue.slice(i + 1));
        break;
      }
    }
    const persisted = writeQuizSaveQueue(remaining);
    if (!persisted) {
      console.warn("[quiz-save] queue:flush:write-failed");
    }
    console.log("[quiz-save] queue:flush:done", {
      remainingItems: remaining.length,
    });
  } finally {
    queueFlushRef.current = false;
  }
}, [authorizedFetch]);

const saveResultToServer = async () => {
  const saveId = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const payload = buildProgressPayload({
    user,
    showResults,
    score,
    maxScore,
    tasks,
    feedback,
    userAnswers,
    taskWhiteboards,
    sectionTitle,
  });

  if (!payload) {
    console.warn("[quiz-save] skipped", {
      saveId,
      hasUser: !!user?.email,
      showResults,
      score,
    });
    return { status: "skipped" };
  }

  console.log("[quiz-save] payload:built", {
    saveId,
    taskId: payload.taskId,
    questionCount: Array.isArray(payload?.snapshot?.questions)
      ? payload.snapshot.questions.length
      : 0,
    whiteboardCount: Array.isArray(payload?.snapshot?.questions)
      ? payload.snapshot.questions.filter((q) => q?.whiteboard).length
      : 0,
    payloadBytes: estimateJsonSize(payload),
  });

  if (typeof navigator !== "undefined" && !navigator.onLine) {
    const enq = enqueueQuizSave(payload);
    console.warn("[quiz-save] queued:offline", {
      saveId,
      taskId: payload.taskId,
      queuedItems: enq.ok ? readQuizSaveQueue().length : "unknown",
      queueWriteOk: enq.ok,
    });
    return enq.ok ? { status: "queued" } : { status: "queue_failed" };
  }

  let lastLiveErr = null;
  for (let attempt = 0; attempt < LIVE_SAVE_RETRY_DELAYS_MS.length; attempt += 1) {
    const waitMs = LIVE_SAVE_RETRY_DELAYS_MS[attempt];
    if (waitMs > 0) {
      await delay(waitMs);
    }
    try {
      await postProgressPayload(payload, { saveId, source: "live", attempt: attempt + 1 });
      await flushQueuedQuizSaves();
      return { status: "saved" };
    } catch (e) {
      lastLiveErr = e;
      console.warn("[quiz-save] live-retry-failed", {
        saveId,
        attempt: attempt + 1,
        error: e?.message || String(e),
      });
    }
  }

  console.error("saveResultToServer error:", lastLiveErr);
  const enq = enqueueQuizSave(payload);
  console.warn("[quiz-save] queued:after-error", {
    saveId,
    taskId: payload.taskId,
    error: lastLiveErr?.message || String(lastLiveErr),
    queuedItems: enq.ok ? readQuizSaveQueue().length : "unknown",
    queueWriteOk: enq.ok,
  });
  return enq.ok ? { status: "queued" } : { status: "queue_failed" };
};

const getSaveResultModalState = (result) => {
  if (result?.status === "saved") {
    return {
      ok: true,
      text: (isMaturaCourseQuiz || isE8CourseQuiz)
        ? "Podejście zostało zapisane i jest dostępne w historii podejść na ścieżce."
        : "Podejście zostało zapisane. Możesz wrócić do niego w sekcji „Mój postęp”.",
    };
  }
  if (result?.status === "queued") {
    return {
      ok: true,
      text:
        "Wynik zostal sprawdzony i zapisany lokalnie. Wyslemy go automatycznie, gdy polaczenie z serwerem bedzie dostepne.",
    };
  }
  if (result?.status === "queue_failed") {
    return {
      ok: false,
      text:
        "Wynik zostal sprawdzony, ale nie udalo sie zapisac go ani na serwerze, ani lokalnie. Sprobuj ponownie.",
    };
  }
  return {
    ok: false,
    text: "Nie udalo sie zapisac wyniku. Sprobuj ponownie.",
  };
};
  useEffect(() => {
    if (!pendingAutoSave) return;
    if (!canSave) {
      setPendingAutoSave(false);
      return;
    }
    if (!showResults || score == null) return;

    (async () => {
      try {
        setSaveInProgress(true);
        setSaveOk(true);
        setSaveModalText("Sprawdzanie zakonczone. Trwa zapisywanie wyniku...");
        setSaveModalOpen(true);

        const result = await saveResultToServer();

        const nextModal = getSaveResultModalState(result);
        setSaveOk(nextModal.ok);
        setSaveModalText(nextModal.text);
        if (result?.status === "saved" || result?.status === "queued") {
          removeLearningDraft(draftKey);
        }
      } finally {
        setSaveInProgress(false);
        setPendingAutoSave(false);
      }
    })();
  }, [pendingAutoSave, canSave, showResults, score]);

  useEffect(() => {
    if (!canSave) return;
    flushQueuedQuizSaves();
  }, [canSave, flushQueuedQuizSaves]);

  const retrySaveResult = async () => {
    try {
      setSaveInProgress(true);
      setSaveOk(true);
      setSaveModalText("Ponawiam zapis wyniku...");
      const result = await saveResultToServer();
      const nextModal = getSaveResultModalState(result);
      setSaveOk(nextModal.ok);
      setSaveModalText(nextModal.text);
      if (result?.status === "saved" || result?.status === "queued") {
        removeLearningDraft(draftKey);
      }
    } finally {
      setSaveInProgress(false);
    }
  };

  useEffect(() => {
    if (!canSave) return;
    const onOnline = () => flushQueuedQuizSaves();
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [canSave, flushQueuedQuizSaves]);

  useEffect(() => {
    if (!canSave) return;
    const timer = setInterval(() => {
      flushQueuedQuizSaves();
    }, 45000);
    return () => clearInterval(timer);
  }, [canSave, flushQueuedQuizSaves]);

  useEffect(() => {
    if (!canSave) return;
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        flushQueuedQuizSaves();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [canSave, flushQueuedQuizSaves]);

  const saveResultManually = () => {
    if (!user || !user.email) {
      alert("Musisz być zalogowany, aby zapisać wynik.");
      return;
    }
    const possible = maxScore ?? tasks.length;
    saveLessonResult(user.email, sectionTitle, score, possible);
    alert("Wynik zapisany!");
  };

  const reset = () => {
    setRerolling(true);
    let nextTasks = tasks;

    if (typeof regenerateTasks === "function") {
      try {
        nextTasks = generateDifferentTaskSet(regenerateTasks, tasks) || tasks;
      } catch (error) {
        console.warn("[quiz-reroll] Nie udało się wygenerować nowego zestawu", error);
      }
    }

    setTasks(nextTasks);
    setUserAnswers(nextTasks.map(() => ""));
    setShowResults(false);
    setShowExplanation(nextTasks.map(() => false));
    setScore(null);
    setMaxScore(null);
    setFeedback([]);
    setTaskWhiteboards(nextTasks.map(() => null));
    setWhiteboardResetToken((prev) => prev + 1);
    setLocalSaveMessage("");

    removeLearningDraft(draftKey);

    if (typeof regenerateTasks === "function") {
      window.setTimeout(() => setRerolling(false), 700);
      return;
    }

    const params = new URLSearchParams(location.search);
    params.set("r", String((Number(params.get("r")) || 0) + 1));
    navigate(
      { pathname: location.pathname, search: params.toString() },
      { replace: true }
    );
  };


  // Render LaTeX pomocniczo (obsługa różnych typów odpowiedzi)
  function toLatex(input) {
    if (input == null || input === "") return "-";

    // [[x1,y1],[x2,y2]]
    if (
      Array.isArray(input) &&
      input.length === 2 &&
      Array.isArray(input[0]) &&
      Array.isArray(input[1])
    ) {
      const [a, b] = input;
      const A = `A' = \\left(${a[0] ?? "?"},\\ ${a[1] ?? "?"}\\right)`;
      const B = `B' = \\left(${b[0] ?? "?"},\\ ${b[1] ?? "?"}\\right)`;
      return `${A},\\ ${B}`;
    }

    // [x,y]
    if (Array.isArray(input)) {
      return `\\left(${input[0] ?? "?"},\\ ${input[1] ?? "?"}\\right)`;
    }

    // jeżeli dostaliśmy obiekt (np. { bUserTrue, cUserTrue }),
    // to na wszelki wypadek nie próbujemy robić .replace na nim
    if (typeof input === "object") {
      return "-";
    }

    // tekstowe √, +, - -> LaTeX
    const s = String(input);

    return s
      .replace(/√\s*\(?([0-9.,]+)\)?/g, (_, rad) => `\\sqrt{${rad.replace(",", ".")}}`)
      .replace(/\s*\+\s*/g, " + ")
      .replace(/\s*\-\s*/g, " - ");
  }

  // --- GLOBAL helper: znajdź w userAnswer dowolny tekst, który wygląda na przedziały i da się uprościć
  function extractAllRawStringsFromAnswer(val, out = []) {
    if (val == null) return out;

    if (typeof val === "string") {
      const s = val.trim();
      if (s) out.push(s);
      return out;
    }

    if (typeof val === "number" || typeof val === "boolean") return out;

    if (Array.isArray(val)) {
      val.forEach((v) => extractAllRawStringsFromAnswer(v, out));
      return out;
    }

    if (typeof val === "object") {
      // typowy wrapper z IntervalInput: { raw, parsed }
      if ("raw" in val && typeof val.raw === "string") {
        const s = val.raw.trim();
        if (s) out.push(s);
      }

      // nie zaglądamy w parsed, bo tam są liczby/Infinity i nie ma sensu
      for (const k of Object.keys(val)) {
        if (k === "parsed") continue;
        extractAllRawStringsFromAnswer(val[k], out);
      }
    }

    return out;
  }

  function getIntervalSimplificationFromUserAnswer(userAnswer) {
    const candidates = extractAllRawStringsFromAnswer(userAnswer, []);

    for (const raw of candidates) {
      const parsed = parseIntervalInput(raw);
      if (
        parsed &&
        parsed.kind === "intervals" &&
        parsed.needsSimplification &&
        parsed.simplifiedLatex
      ) {
        return parsed.simplifiedLatex;
      }
    }

    return null;
  }

  // GLOBAL helper: wykrywa wpisany przedział od większej do mniejszej liczby
  function hasInvalidIntervalOrderInUserAnswer(userAnswer) {
    const candidates = extractAllRawStringsFromAnswer(userAnswer, []);
    for (const raw of candidates) {
      const parsed = parseIntervalInput(raw);
      if (parsed && parsed.kind === "invalid_order") return true;
    }
    return false;
  }


  // style komunikatów – bez tła, tylko kolory (jak dawniej)
  const msgStyle = {
    base: { fontWeight: "bold", marginBottom: "8px" },
    ok: { color: "limegreen" },  // pełna poprawność
    warn: { color: "orange" },   // częściowo poprawna
    bad: { color: "crimson" },   // niepoprawna
  };

  // komponent z ikonkami per-część (po prawej od odpowiedzi)
  function PartsColumn({ parts }) {
    if (!parts) return null;
    const entries = Object.entries(parts);
    return (
      <div style={{ minWidth: 140, paddingLeft: 12 }}>
        {entries.map(([label, ok]) => (
          <div
            key={label}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 8,
              lineHeight: 1.3,
              marginBottom: 4,
              opacity: 0.95,
            }}
          >
            <span style={{ fontSize: 18 }}>{ok ? "✅" : "❌"}</span>
            <span style={{ fontSize: 13 }}>{label}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <MatematronModal
        open={rerolling}
        variant="info"
        title="Losowanie nowych zadań"
        hideFooter
        maxWidth="min(460px, calc(100vw - 32px))"
      >
        <div
          className="universal-quiz-reroll-content"
          role="status"
          aria-live="polite"
        >
          <span className="universal-quiz-reroll-spinner" aria-hidden="true" />
          <strong>Przygotowuję nowy zestaw…</strong>
          <span>Za chwilę zobaczysz nowy zestaw zadań i danych.</span>
        </div>
      </MatematronModal>
      {/* FAB-y – render do body, więc są przy krawędziach strony */}
      {!hideUtilityFabs && !calcOpen && (
        <Fab
          side="left"
          top={fabTop}
          title="Kalkulator (Ctrl+Alt+K)"
          ariaLabel="Otwórz kalkulator"
          onClick={() => setCalcOpen(true)}
          icon={
            <svg width="28" height="28" viewBox="0 0 24 24" aria-hidden="true">
              <rect x="3" y="2.5" width="18" height="19" rx="3" fill="currentColor" opacity=".18" />
              <rect x="6" y="5.5" width="12" height="3.4" rx="1" fill="currentColor" />
              <circle cx="8.25" cy="11.5" r="1.2" fill="currentColor" />
              <circle cx="12"   cy="11.5" r="1.2" fill="currentColor" />
              <circle cx="15.75" cy="11.5" r="1.2" fill="currentColor" />
              <circle cx="8.25" cy="15.25" r="1.2" fill="currentColor" />
              <circle cx="12"   cy="15.25" r="1.2" fill="currentColor" />
              <circle cx="15.75" cy="15.25" r="1.2" fill="currentColor" />
            </svg>
          }
        />
      )}

      {!hideUtilityFabs && !sheetOpen && (
        <Fab
          side="right"
          top={fabTop}
          title="Karta wzorów (Ctrl+Alt+W)"
          ariaLabel="Otwórz kartę wzorów"
          onClick={() => setSheetOpen(true)}
          icon={
            <svg width="28" height="28" viewBox="0 0 24 24" aria-hidden="true">
              <rect x="4" y="3" width="14" height="18" rx="2" fill="currentColor" opacity=".18" />
              <rect x="6.5" y="6" width="9" height="1.8" rx=".9" fill="currentColor" />
              <rect x="6.5" y="9.2" width="9" height="1.8" rx=".9" fill="currentColor" />
              <rect x="6.5" y="12.4" width="9" height="1.8" rx=".9" fill="currentColor" />
              <rect x="6.5" y="15.6" width="6" height="1.8" rx=".9" fill="currentColor" />
            </svg>
          }
        />
      )}

      <hr
        style={{
          margin: "24px 0",
          border: "none",
          borderTop: "2px solid",
          borderColor: "var(--color-text)",
        }}
      />

      {tasks.map((task, i) => {
        const adapter = registryToUse[task.type];
        if (!adapter)
          return <p key={i}>Nieobsługiwany typ zadania: {task.type}</p>;

        const fb = showResults ? feedback[i] : null;
        const isCorrect = fb ? fb.isCorrect : null;

        const hasPartial =
          fb &&
          typeof fb.score === "number" &&
          typeof fb.maxScore === "number" &&
          fb.score > 0 &&
          fb.score < fb.maxScore;

        const needsSimplification = fb ? fb.needsSimplification : null;
        const expectedLatex = fb ? fb.expectedLatex : null;

        const partialInfo =
          fb && typeof fb.score === "number" && typeof fb.maxScore === "number"
            ? ` (wynik: ${fb.score}/${fb.maxScore})`
            : "";

        const formatTaskLabel = (task, index) => {
          const base =
            task && typeof task.displayBaseNumber === "number"
              ? task.displayBaseNumber
              : index + 1;

          if (
            task &&
            task.maturaGroupId &&
            typeof task.maturaGroupPart === "number"
          ) {
            return `${base}.${task.maturaGroupPart}`;
          }
          return `${base}`;
        };

        // 🔢 liczba punktów za zadanie – najpierw patrzymy na data.maxPoints,
        // a jeśli go brak, próbujemy odczytać maxScore z checkAnswer.
        const taskMaxPoints = (() => {
          // 1) twardo ustawione w danych zadania
          if (
            task &&
            task.data &&
            typeof task.data.maxPoints === "number"
          ) {
            return task.data.maxPoints;
          }

          // 2) spróbujmy zapytać adapter.checkAnswer o maxScore
          if (adapter && typeof adapter.checkAnswer === "function") {
            try {
              // przekazujemy "pustą" odpowiedź – ważne, żeby checkAnswer był czysty
              const probe = adapter.checkAnswer(task, "");
              if (
                probe &&
                typeof probe === "object" &&
                typeof probe.maxScore === "number" &&
                probe.maxScore > 0
              ) {
                return probe.maxScore;
              }
            } catch (e) {
              // jeśli coś pójdzie nie tak, po prostu lecimy na domyślne 1
            }
          }

          // 3) domyślnie 1 punkt
          return 1;
        })();

        function renderTextWithInlineMath(message) {
        if (!message) return null;
        const s = String(message);

        // rozbij na linie (aby \n było widoczne), a każdą linię na tekst / matematykę $...$
        const lines = s.split(/\r?\n/);

        return lines.map((line, lineIdx) => {
          const parts = line.split(/(\$[^$]+\$)/g).filter(Boolean);
          return (
            <React.Fragment key={`line-${lineIdx}`}>
              {parts.map((part, idx) => {
                const isMath = part.startsWith("$") && part.endsWith("$");
                if (!isMath) return <React.Fragment key={`txt-${lineIdx}-${idx}`}>{part}</React.Fragment>;
                const math = part.slice(1, -1); // bez $
                return <InlineMath key={`math-${lineIdx}-${idx}`} math={math} />;
              })}
              {lineIdx < lines.length - 1 ? <br /> : null}
            </React.Fragment>
          );
        });
      }


        return (
          <div key={i}>
            <div
              className="p-4 border rounded shadow-sm bg-white text-black"
              style={{
                marginTop: i === 0 ? "24px" : "0",
                marginBottom: "16px",
              }}
            >
              <strong className="mb-3 block text-lg">
                Zadanie {formatTaskLabel(task, i)}{" "}
                <span
                  style={{
                    fontWeight: "normal",
                    fontSize: "0.9em",
                  }}
                >
                  ({taskMaxPoints} pkt)
                </span>
              </strong>


              <div className="mb-3">{adapter.renderTask(task)}</div>
              {showResults && task.basicCourseSource && <MaturaOriginalSourceLinks task={task} />}

              {!showResults ? (
                <div className="mb-3">
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "flex-end",
                      gap: 8,
                      width: "100%",
                    }}
                  >
                    <div ref={(element) => { answerContainers.current[i] = element; }} style={{ flex: "1 1 320px", minWidth: 0 }}>
                      {adapter.renderInput(
                        task,
                        userAnswers[i],
                        (val) => handleChange(i, val),
                        false
                      )}
                    </div>
                    {!inputOwnsWhiteboard(task) && <TaskInlineWhiteboard
                      alignRight
                      renderTaskPreview={() => adapter.renderTask(task)}
                      initialBoardState={taskWhiteboards[i]}
                      onBoardStateChange={(boardState) => handleTaskWhiteboardChange(i, boardState)}
                      resetToken={whiteboardResetToken}
                    />}
                  </div>
                </div>
              ) : (
                <>
                  {/* Twoja odpowiedź + kolumna z ikonami per-część (jeśli są) */}
                  <div
                    className="mb-2"
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 12,
                    }}
                  >
                    <div className="matura-answer-result" style={{ flex: 1, minWidth: 0 }}>
                      <strong>Twoja odpowiedź:</strong>{" "}
                      {!detectHasAnswer(userAnswers[i]) ? (
                        <i style={{ opacity: 0.75 }}>(brak odpowiedzi)</i>
                      ) : adapter.renderUserAnswer ? (
                        adapter.renderUserAnswer(userAnswers[i], task, fb)
                      ) : Array.isArray(userAnswers[i]) ? (
                        <InlineMath math={toLatex(userAnswers[i])} />
                      ) : userAnswers[i]?.a && userAnswers[i]?.b ? (
                        <InlineMath
                          math={`a = ${formatLatexPreview(
                            userAnswers[i].a.raw
                          )}, b = ${formatLatexPreview(userAnswers[i].b.raw)}`}
                        />
                      ) : (
                        <InlineMath
                          math={
                            typeof userAnswers[i] === "object" &&
                            userAnswers[i]?.raw
                              ? formatLatexPreview(
                                  userAnswers[i].raw,
                                  task.type === "FindYInterceptTask" ? "b" : "a"
                                )
                              : toLatex(userAnswers[i])
                          }
                        />
                      )}
                    </div>

                    {/* prawa kolumna: ✅/❌ dla części, jeśli adapter zwrócił parts */}
                    {fb?.parts && <PartsColumn parts={fb.parts} />}
                  </div>

                  {/* komunikat główny: zielony / pomarańczowy / czerwony (bez tła) */}
                  {isCorrect ? (
                    <>
                      <p style={{ ...msgStyle.base, ...msgStyle.ok }}>
                        ✔️ Poprawna{partialInfo}
                      </p>
                    </>
                  ) : hasPartial ? (
                    <>
                      <p style={{ ...msgStyle.base, ...msgStyle.warn }}>
                        🟠 Częściowo poprawna{partialInfo}
                      </p>
                      {!adapter.hideCorrectAnswerWhenUserAnswer && (
                        <div className="mb-2 matura-answer-result">
                          <strong>Poprawna odpowiedź:</strong>{" "}
                          {adapter.renderAnswer(task)}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <p style={{ ...msgStyle.base, ...msgStyle.bad }}>
                        ❌ Niepoprawna{partialInfo}
                      </p>
                      {!adapter.hideCorrectAnswerWhenUserAnswer && (
                        <div className="mb-2 matura-answer-result">
                          <strong>Poprawna odpowiedź:</strong>{" "}
                          {adapter.renderAnswer(task)}
                        </div>
                      )}
                    </>
                  )}

                  {fb?.message && fb?.messageType !== "empty" && (
                    <p
                      style={{
                        marginTop: -2,
                        marginBottom: 10,
                        color: "var(--color-text)",
                        fontWeight: 600,
                      }}
                    >
                      {renderTextWithInlineMath(fb.message)}
                    </p>
                  )}

                  {/* GLOBAL: komunikat o odwróconym przedziale (dopiero po sprawdzeniu) */}
                  {!isCorrect && !fb?.message && hasInvalidIntervalOrderInUserAnswer(userAnswers[i]) && (
                    <p
                      style={{
                        marginTop: -2,
                        marginBottom: 10,
                        color: "var(--color-text)",
                        fontWeight: 600,
                      }}
                    >
                      {renderTextWithInlineMath(
                        "Przedział zapisujemy od mniejszej do większej liczby (lewy koniec $\\le$ prawy koniec)."
                      )}
                    </p>
                  )}

                  {/* GLOBAL: komunikat o możliwości uproszczenia zapisu z IntervalInput (po sprawdzeniu) */}
                  {isCorrect && !fb?.message && (() => {
                    const simplifiedLatex = getIntervalSimplificationFromUserAnswer(userAnswers[i]);
                    if (!simplifiedLatex) return null;

                    return (
                      <p style={{ color: "darkorange", marginBottom: "8px" }}>
                        🔔 Można zapisać prościej jako:{" "}
                        <InlineMath math={`x\\in\\;${simplifiedLatex}`} />
                      </p>
                    );
                  })()}

                  <div
                    style={{
                      marginTop: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    <button
                      className="btn-secondary btn-task-action"
                      onClick={() => toggleExplanation(i)}
                    >
                      {showExplanation[i]
                        ? "Ukryj wyjaśnienie"
                        : "Pokaż wyjaśnienie"}
                    </button>

                    <TaskInlineWhiteboard
                      alignRight
                      renderTaskPreview={() => adapter.renderTask(task)}
                      initialBoardState={inputOwnsWhiteboard(task) ? userAnswers[i]?.whiteboard || taskWhiteboards[i] : taskWhiteboards[i] || userAnswers[i]?.whiteboard}
                      onBoardStateChange={(boardState) =>
                        handleTaskWhiteboardChange(i, boardState)
                      }
                      resetToken={whiteboardResetToken}
                    />

                  {showExplanation[i] && (
                    <div style={{ marginTop: "12px", width: "100%", flexBasis: "100%" }}>
                      <HorizontalScroll rightPadding={48}>
                        <div className="mb-2 matura-explanation">
                          {adapter.renderExplanation({
                            ...task,
                            type: task.type,
                            data: task.data,
                            answer: Array.isArray(task.answer)
                              ? task.answer
                              : Array.isArray(task.answer?.answer)
                              ? task.answer.answer
                              : task.answer,
                          })}
                        </div>
                      </HorizontalScroll>
                    </div>
                  )}

                  </div>
                </>
              )}
            </div>

            <hr
              style={{
                margin: "24px 0",
                border: "none",
                borderTop: "2px solid",
                borderColor: "var(--color-text)",
              }}
            />
          </div>
        );
      })}

      <MatematronModal open={confirmIncomplete} variant="info" title="Nie wszystkie odpowiedzi są uzupełnione" message="Czy mimo to sprawdzić odpowiedzi?" primaryText="Wróć do uzupełniania" onPrimary={() => setConfirmIncomplete(false)} secondaryText="Sprawdź mimo to" onSecondary={() => checkAndSave(true)} onClose={() => setConfirmIncomplete(false)} />
      {!showResults ? (
        <div style={{ textAlign: "center", marginTop: "30px" }}>
          <div style={{ display: "flex", justifyContent: "center", gap: "16px" }}>
            <button className="btn-secondary" onClick={checkAndSave}>
              {forceLocalMode
                ? (typeof onLocalSnapshot === "function" ? "Sprawdź i zapisz" : "Sprawdź odpowiedzi")
                : canSave
                ? "Sprawdź i zapisz"
                : "Sprawdź odpowiedzi"}
            </button>
          </div>
        </div>
      ) : (
        <div style={{ textAlign: "center", marginTop: "30px" }}>
          <div style={{ fontSize: "18px", marginBottom: "16px" }}>
            <strong>
              Twój wynik: {score} / {maxScore ?? tasks.length} punktów
            </strong>
          </div>
          {forceLocalMode && localSaveMessage ? (
            <div
              style={{
                margin: "0 auto 16px",
                maxWidth: 520,
                padding: "8px 12px",
                borderRadius: 10,
                border: "1px solid rgba(255, 153, 0, 0.34)",
                background: "rgba(255, 153, 0, 0.08)",
                color: "var(--color-text)",
                fontWeight: 700,
              }}
            >
              {localSaveMessage}
            </div>
          ) : null}

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "16px",
              flexWrap: "wrap",
            }}
          >
            <button className="btn-secondary" onClick={reset}>
              Spróbuj ponownie
            </button>
          </div>
        </div>
      )}

      {/* Kalkulator – lewa krawędź (z przyciskiem do karty wzorów) */}
      <EdgeDrawer
        side="left"
        isOpen={calcOpen}
        onOpenChange={setCalcOpen}
        width={420}
        title="Kalkulator maturalny"
        showHandle={false}
        offsetTop={0}
        background="linear-gradient(180deg, rgba(255,153,0,0.36) 0%, rgba(255,190,70,0.36) 100%)"
        contentMaxWidth={380}
        zIndex={sheetOpen ? 10002 : 1000}
        showBackdrop={false}
        headerRight={
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setSheetOpen(true)}
            style={{ fontSize: 14, padding: "2px 10px", lineHeight: 1, borderRadius: 10 }}
            title="Otwórz kartę wzorów (Ctrl+Alt+W)"
          >
            Karta wzorów
          </button>
        }
      >
        {calcOpen ? <ExamCalculator embedded /> : null}
      </EdgeDrawer>

      {/* Karta wzorów – prawa krawędź (z przyciskiem do kalkulatora) */}
      <FormulaSheet
        isOpen={sheetOpen}
        onOpenChange={setSheetOpen}
        pdfPath={WZORY_URL}
        showBackdrop={false}
        headerRightExtra={
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setCalcOpen(true)}
            style={{ fontSize: 14, padding: "2px 10px", lineHeight: 1, borderRadius: 10 }}
            title="Otwórz kalkulator (Ctrl+Alt+K)"
          >
            Kalkulator
          </button>
        }
      />
        <MatematronModal
          open={unsavedAttemptGuard.modalOpen}
          variant="info"
          title="Niesprawdzone odpowiedzi"
          message={UNSAVED_QUIZ_MESSAGE}
          primaryText="Zostań w quizie"
          onPrimary={unsavedAttemptGuard.stayInQuiz}
          secondaryText="Wyjdź bez zapisywania"
          onSecondary={unsavedAttemptGuard.leaveQuiz}
          onClose={unsavedAttemptGuard.stayInQuiz}
          maxWidth="min(620px, calc(100vw - 32px))"
        />
        <MatematronModal
          open={saveModalOpen}
          variant={saveInProgress ? "info" : saveOk ? "success" : "error"}
          title={
            saveInProgress
              ? "Zapisywanie wyniku"
              : saveOk
              ? (() => {
                  const s = typeof score === "number" ? score : 0;
                  const m =
                    typeof maxScore === "number" && Number.isFinite(maxScore)
                      ? maxScore
                      : tasks.length;

                  const fmt = (n) => String(n).replace(/\\.0+$/, "");
                  return `Wynik zapisany - ${fmt(s)} / ${fmt(m)} pkt`;
                })()
              : undefined
          }
          message={saveModalText}
          hideFooter={saveInProgress}
          primaryText={saveInProgress ? undefined : saveOk ? localSnapshotId ? "Zamknij" : "OK" : "Spróbuj ponownie"}
          onPrimary={
            saveInProgress
              ? undefined
              : saveOk
              ? () => {
                  setSaveModalOpen(false);
                  if (localSnapshotId) return;
                  onSavedResultDismissed?.({ score, maxScore, tasks, feedback });
                }
              : () => retrySaveResult()
          }
          secondaryText={
            saveInProgress
              ? undefined
              : saveOk
              ? onSavedResultDismissed
                ? "Dalej"
                : (isMaturaCourseQuiz || isE8CourseQuiz)
                ? "Wróć do ścieżki"
                : canSave
                ? "Mój postęp"
                : undefined
              : "Anuluj"
          }
          onSecondary={
            !saveInProgress && saveOk
              ? () => {
                  setSaveModalOpen(false);
                  if (onSavedResultDismissed) {
                    onSavedResultDismissed({ score, maxScore, tasks, feedback });
                  } else {
                    navigate(savedResultDestination);
                    setTimeout(() => window.scrollTo({ top: 0, left: 0, behavior: "auto" }), 0);
                  }
                }
              : !saveInProgress
              ? () => setSaveModalOpen(false)
              : undefined
          }
          extraActionText={
            !saveInProgress && saveOk
              ? localSnapshotId
                ? undefined
                : "Spróbuj ponownie — nowe zadania"
              : undefined
          }
          onExtraAction={
            !saveInProgress && saveOk
              ? () => {
                  setSaveModalOpen(false);
                  reset();
                }
              : undefined
          }
          onClose={saveInProgress ? undefined : () => {
            setSaveModalOpen(false);
            if (saveOk) onSavedResultDismissed?.({ score, maxScore, tasks, feedback });
          }}
        />
    </div>
  );
}
